import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { stockMovements as seedMovements } from '../data/mockData';
import { useAuth } from './AuthContext';
import {
  listAllProducts,
  listProductsUpdatedSince,
  createProduct as apiCreateProduct,
  getProduct as apiGetProduct,
  updateProduct as apiUpdateProduct,
  uploadProductImage,
  uploadProductImages,
  deleteProductImage,
  keyFromImageUrl,
} from '../api/inventory';
import { newestUpdatedAt, posCatalogCache } from '../utils/posCatalogCache';
import { collectBarcodeAliases, isMergedProduct, splitMergedDelta } from '../utils/productMerge';
import {
  listCategories as apiListCategories,
  createCategory as apiCreateCategory,
  updateCategory as apiUpdateCategory,
  deleteCategory as apiDeleteCategory,
  uploadCategoryImage,
  deleteCategoryImage,
} from '../api/categories';
import { GST_RATES } from '../utils/gst';

export const ProductsContext = createContext(null);

const PALETTE = ['#F2B922', '#2E7D32', '#2F6FED', '#7C3AED', '#0D9488', '#D9724B', '#B8850A'];
const EMOJIS = ['🛒', '📦', '🧺', '🥫', '🧴', '🍞', '🧃'];

let nextSwatch = 0;
let nextCategorySwatch = 0;
let nextMoveId = 900;

function withKeptSwatch(item, previous, decorate) {
  const old = previous?.find((product) => product.id === item.id);
  if (old?.swatch && old?.emoji) {
    return { ...old, ...item, swatch: old.swatch, emoji: old.emoji };
  }
  return decorate(item);
}

function priceFromCache(item, storedById) {
  const cached = storedById.get(item.id);
  if (cached?.priceSource !== 'lot') return item;
  const catalogPrice = Number(item.price);
  const lotPrice = Number(cached.price);
  const catalogMrp = Number(item.mrp);
  const lotMrp = Number(cached.mrp);
  const priceDiffers = Number.isFinite(catalogPrice) && Number.isFinite(lotPrice)
    && Math.abs(catalogPrice - lotPrice) >= 0.001;
  const mrpDiffers = Number.isFinite(catalogMrp) && Number.isFinite(lotMrp)
    && Math.abs(catalogMrp - lotMrp) >= 0.001;
  // A saved catalog price is what Products shows. Don't hide it behind an older shelf lot.
  if (priceDiffers || mrpDiffers) return item;
  return { ...item, price: cached.price, mrp: cached.mrp, priceSource: 'lot' };
}

export function ProductsProvider({ children }) {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [movements, setMovements] = useState(seedMovements);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');

  const decorate = useCallback((product) => {
    if (product.swatch && product.emoji) return product;
    const idx = nextSwatch++;
    return { ...product, swatch: product.swatch || PALETTE[idx % PALETTE.length], emoji: product.emoji || EMOJIS[idx % EMOJIS.length] };
  }, []);

  const decorateCategory = useCallback((category) => {
    if (category.color) return category;
    const idx = nextCategorySwatch++;
    return { ...category, color: PALETTE[idx % PALETTE.length] };
  }, []);

  const loadProducts = useCallback(async () => {
    if (!user?.idToken) return [];
    setLoading(true);
    setError('');
    try {
      const items = await listAllProducts(user.idToken, { includeInactive: true });
      await posCatalogCache.upsert(items, { source: 'meta' }).catch(() => {});
      const stored = await posCatalogCache.hydrate().catch(() => []);
      const storedById = new Map(stored.map((row) => [row.id, row]));
      const priced = items
        .filter((item) => !isMergedProduct(item))
        .map((item) => priceFromCache(item, storedById));
      setProducts((prev) => priced.map((item) => withKeptSwatch(item, prev, decorate)));
      return priced;
    } catch (err) {
      setError(err.message || 'Could not load products.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.idToken, decorate]);

  const syncCatalog = useCallback(async () => {
    if (!user?.idToken) return [];
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return [];
    let existing = [];
    try {
      existing = await posCatalogCache.hydrate();
    } catch {
      existing = [];
    }
    if (!existing.length || !newestUpdatedAt(existing)) return loadProducts();
    try {
      const delta = await listProductsUpdatedSince(user.idToken, newestUpdatedAt(existing));
      const { live, mergedIds } = splitMergedDelta(delta);
      if (mergedIds.length) await posCatalogCache.remove(mergedIds);
      if (live.length) await posCatalogCache.upsert(live, { source: 'meta' });
      const stored = await posCatalogCache.hydrate();
      const storedById = new Map(stored.map((row) => [row.id, row]));
      setProducts((prev) => {
        const drop = new Set(mergedIds);
        const map = new Map(prev.filter((product) => !drop.has(product.id)).map((product) => [product.id, product]));
        live.forEach((item) => {
          const next = withKeptSwatch(priceFromCache(item, storedById), prev, decorate);
          map.set(item.id, next);
        });
        return [...map.values()];
      });
      return delta;
    } catch (err) {
      if (!existing.length) setError(err.message || 'Could not load products.');
      return [];
    }
  }, [user?.idToken, decorate, loadProducts]);

  const forgetProducts = useCallback((ids) => {
    const drop = new Set((ids || []).filter(Boolean));
    if (!drop.size) return;
    setProducts((prev) => prev.filter((product) => !drop.has(product.id)));
    posCatalogCache.remove([...drop]).catch(() => {});
  }, []);

  const mergeProducts = useCallback((incoming) => {
    if (!incoming?.length) return;
    setProducts((prev) => {
      const map = new Map(prev.map((p) => [p.id || p.sku, p]));
      const written = [];
      incoming.forEach((raw) => {
        const item = decorate(raw);
        const key = item.id || item.sku;
        if (!key) return;
        const previous = map.get(key);
        const barcodeAliases = collectBarcodeAliases(item.barcode || previous?.barcode, [
          ...(previous?.barcodeAliases || []),
          ...(item.barcodeAliases || []),
        ]);
        const merged = {
          ...previous,
          ...item,
          ...(barcodeAliases.length ? { barcodeAliases } : {}),
        };
        map.set(key, merged);
        written.push(merged);
      });
      posCatalogCache.upsert(written, { source: 'meta' }).catch(() => {});
      return [...map.values()];
    });
  }, [decorate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.idToken) return;
      let cached = [];
      try {
        cached = await posCatalogCache.hydrate();
      } catch {
        cached = [];
      }
      if (cancelled) return;
      if (cached.length) {
        setProducts((prev) => cached
          .filter((item) => !isMergedProduct(item))
          .map((item) => withKeptSwatch(item, prev, decorate)));
        setLoading(false);
        await syncCatalog();
        return;
      }
      await loadProducts();
    })();
    return () => { cancelled = true; };
  }, [user?.idToken, decorate, loadProducts, syncCatalog]);

  const loadCategories = useCallback(async () => {
    if (!user?.idToken) return;
    setCategoriesLoading(true);
    setCategoriesError('');
    try {
      const items = await apiListCategories(user.idToken, { includeInactive: true });
      setCategories(items.map(decorateCategory));
    } catch (err) {
      setCategoriesError(err.message || 'Could not load categories.');
    } finally {
      setCategoriesLoading(false);
    }
  }, [user?.idToken, decorateCategory]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const addProduct = useCallback(async (data) => {
    const created = await apiCreateProduct(data, user.idToken);
    const pendingFiles = (data.images || []).filter((img) => img.file).map((img) => img.file);
    let images = data.images || [];
    let latest = created;
    // Follow-up PATCH activates and attaches images. Omit stock so it cannot
    // replace the opening lot created by POST stock_qty.
    const activateBody = { ...data, status: 'ACTIVE' };
    delete activateBody.stock;
    if (pendingFiles.length) {
      const imageKeys = await uploadProductImages(created.id, pendingFiles, user.idToken);
      // The API requires `name` (and the rest of the record) on every PATCH, so re-send the
      // full create payload alongside the new image_keys rather than a bare {image_keys} patch.
      // Fold the onboarding "activate" step into the same call when there's already a PATCH to make.
      latest = await apiUpdateProduct(created.id, { ...activateBody, imageKeys }, user.idToken);
      images = latest.images.length ? latest.images : images;
    } else if (created.status !== 'ACTIVE') {
      latest = await apiUpdateProduct(created.id, activateBody, user.idToken);
      images = latest.images.length ? latest.images : images;
    }

    // Prefer server image_urls after create/import so refresh keeps the cover.
    if (!latest.images.length && (data.imageUrl || images.some((img) => img.url && !img.file))) {
      try {
        const fresh = await apiGetProduct(created.id, user.idToken);
        if (fresh.images?.length) {
          latest = fresh;
          images = fresh.images;
        }
      } catch {
        // Keep form images in session; list refresh may still miss until BE finishes import.
      }
    } else if (latest.images.length) {
      images = latest.images;
    }

    const product = decorate({ ...latest, images });
    setProducts((prev) => [product, ...prev]);
    return product;
  }, [user?.idToken, decorate]);

  const updateProduct = useCallback(async (id, patch) => {
    const current = products.find((product) => product.id === id);
    const removals = patch.imagesToDelete || [];
    for (const img of removals) {
      // eslint-disable-next-line no-await-in-loop
      await deleteProductImage(id, keyFromImageUrl(img.url), user.idToken);
    }

    const orderedImages = Array.isArray(patch.images) ? patch.images : [];
    const prevUrls = (current?.images || []).map((img) => img.url).filter(Boolean);
    const nextRemoteUrls = orderedImages.filter((img) => img.url && !img.file).map((img) => img.url);
    const remotesReordered = nextRemoteUrls.length > 0
      && nextRemoteUrls.length === prevUrls.filter((url) => nextRemoteUrls.includes(url)).length
      && nextRemoteUrls.some((url, idx) => {
        const keptPrev = prevUrls.filter((u) => nextRemoteUrls.includes(u));
        return url !== keptPrev[idx];
      });
    let sawNewFile = false;
    let newFileBeforeRemote = false;
    orderedImages.forEach((img) => {
      if (img.file) sawNewFile = true;
      else if (img.url && sawNewFile) newFileBeforeRemote = true;
    });
    const rewriteOrderedKeys = remotesReordered || newFileBeforeRemote;

    let imageKeys;
    if (rewriteOrderedKeys) {
      // Full ordered key list so cover / sequence matches the form (not append-only).
      imageKeys = [];
      for (const img of orderedImages) {
        if (img.file) {
          // eslint-disable-next-line no-await-in-loop
          imageKeys.push(await uploadProductImage(id, img.file, user.idToken));
        } else if (img.url) {
          imageKeys.push(keyFromImageUrl(img.url));
        }
      }
      if (!imageKeys.length) imageKeys = undefined;
    } else {
      const pendingFiles = orderedImages.filter((img) => img.file).map((img) => img.file);
      imageKeys = pendingFiles.length
        ? await uploadProductImages(id, pendingFiles, user.idToken)
        : undefined;
    }

    // Never re-send a bare image_url for images already on the product — omit from PATCH.
    const patchForApi = { ...patch };
    if (!patchForApi.imageUrl) delete patchForApi.imageUrl;
    // Quantity on hand is changed only from Inventory (stock in / out / adjust).
    delete patchForApi.stock;
    if (current) {
      patchForApi.description = current.description || '';
      patchForApi.lowStockThreshold = current.lowStockThreshold ?? '';
      patchForApi.netQty = current.netQty ?? '';
      // images / imagesToDelete stay from the form so multi-image edits apply.
    }

    const apiBody = { ...patchForApi, imageKeys };

    let updated = await apiUpdateProduct(id, apiBody, user.idToken);
    if (patch.itemType === 'food' || patch.itemType === 'general') {
      updated = { ...updated, itemType: patch.itemType };
    }
    const hsn = String(patch.hsnCode || patch.hsn || '').trim();
    const completingStub = current?.catalogComplete === false
      && /^[0-9]{4,8}$/.test(hsn)
      && hsn !== '0000';
    const imagesMutated = Boolean(imageKeys) || removals.length > 0 || Boolean(patch.imageUrl) || remotesReordered;

    if (completingStub || imagesMutated) {
      // PATCH / image attach can return a stale eventually-consistent reread.
      try {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          const completeOk = !completingStub || updated.catalogComplete !== false;
          const imagesOk = !imagesMutated || updated.images?.length > 0
            || (removals.length > 0 && !(patch.images || []).some((img) => img.url && !img.file) && !imageKeys);
          if (completeOk && imagesOk) break;
          if (attempt > 0) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, attempt * 200));
          }
          // eslint-disable-next-line no-await-in-loop
          updated = await apiGetProduct(id, user.idToken);
        }
      } catch {
        // PATCH succeeded; a later catalog refresh will reconcile.
      }
    }

    // If PATCH omitted image_urls, keep form remotes unless the cashier cleared them.
    const keptRemote = (patch.images || []).filter((img) => img.url && !img.file);
    const intentionallyCleared = removals.length > 0 && keptRemote.length === 0 && !imageKeys && !patch.imageUrl;
    let nextImages = updated.images;
    if (!nextImages.length && !intentionallyCleared) {
      nextImages = keptRemote.length ? keptRemote : (current?.images || []);
      try {
        const fresh = await apiGetProduct(id, user.idToken);
        if (fresh.images?.length) {
          updated = fresh;
          nextImages = fresh.images;
        }
      } catch {
        // Keep best-effort client images; a later list refresh will reconcile.
      }
    }

    const savedType = patch.itemType === 'food' || patch.itemType === 'general' ? patch.itemType : undefined;
    // Catalog META only. Shelf lots stay until the product editor confirms POST /reprice.
    // A meta upsert keeps a previously cached lot price (POS still charges the lot).
    posCatalogCache.upsert([updated], { source: 'meta' }).catch(() => {});
    setProducts((prev) => prev.map((p) => (p.id === id ? decorate({
      ...p,
      ...updated,
      itemType: savedType || updated.itemType,
      ...(savedType === 'general' ? {
        nutritionServingSize: '',
        nutritionEnergyKcal: '',
        nutritionProteinG: '',
        nutritionCarbsG: '',
        nutritionFatG: '',
      } : {}),
      images: nextImages,
    }) : p)));
  }, [user?.idToken, products, decorate]);

  const deleteProduct = useCallback(async (id) => {
    const current = products.find((p) => p.id === id);
    const gstRate = GST_RATES.includes(Number(current?.gstRate))
      ? Number(current.gstRate)
      : GST_RATES[0];
    // Soft-delete via PATCH — keep row in state so category links stay visible.
    const updated = await apiUpdateProduct(id, {
      name: current?.name,
      status: 'INACTIVE',
      gstRate,
      category: current?.category,
      price: current?.price,
      mrp: current?.mrp,
      unit: current?.unit,
    }, user.idToken);
    setProducts((prev) => prev.map((p) => (p.id === id ? decorate({ ...p, ...updated, status: 'INACTIVE' }) : p)));
  }, [user?.idToken, products, decorate]);

  const recordMovement = useCallback((productId, type, qty, reason, user_ = 'You', { syncStock = true } = {}) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    const delta = type === 'OUT' ? -Math.abs(qty) : type === 'ADJUST' ? qty : Math.abs(qty);
    const roundStock = (n) => Math.round(Math.max(0, n) * 1000) / 1000;
    if (syncStock) {
      const nextStock = roundStock(product.stock + delta);
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, stock: nextStock } : p)));
    }
    setMovements((prev) => [{
      id: `M-${nextMoveId++}`,
      productId,
      productName: product.name,
      type,
      qty: type === 'ADJUST' ? delta : Math.abs(qty),
      unit: product.unit,
      soldBy: product.soldBy,
      reason,
      user: user_,
      date: new Date().toISOString(),
    }, ...prev]);
  }, [products]);

  const addCategory = useCallback(async (data) => {
    const created = await apiCreateCategory(data, user.idToken);
    let latest = created;
    if (data.imageFile) {
      latest = await uploadCategoryImage(created.id, data.imageFile, user.idToken);
    }
    const category = decorateCategory({ ...latest, color: data.color, defaultGstRate: data.defaultGstRate });
    setCategories((prev) => [...prev, category]);
    return category;
  }, [user?.idToken, decorateCategory]);

  const updateCategory = useCallback(async (id, patch) => {
    let updated = await apiUpdateCategory(id, patch, user.idToken);
    if (patch.removeImage) {
      updated = await deleteCategoryImage(id, user.idToken);
    } else if (patch.imageFile) {
      updated = await uploadCategoryImage(id, patch.imageFile, user.idToken);
    }
    setCategories((prev) => prev.map((c) => (c.id === id ? {
      ...c,
      ...updated,
      imageUrl: patch.removeImage ? '' : (updated.imageUrl || c.imageUrl),
      color: patch.color ?? c.color,
      defaultGstRate: patch.defaultGstRate ?? c.defaultGstRate,
    } : c)));
    return updated;
  }, [user?.idToken]);

  const deleteCategory = useCallback(async (id) => {
    if (String(id).toLowerCase() === 'general') {
      const err = new Error('category_protected');
      err.code = 'category_protected';
      err.categoryId = id;
      throw err;
    }
    await apiDeleteCategory(id, user.idToken);
    setCategories((prev) => prev.filter((c) => c.id !== id));
  }, [user?.idToken]);

  const applyProductCategoryMoves = useCallback((moves) => {
    if (!moves?.length) return;
    const map = new Map(moves.map((m) => [m.id, m.category]));
    setProducts((prev) => prev.map((p) => (map.has(p.id) ? { ...p, category: map.get(p.id) } : p)));
  }, []);

  const value = useMemo(() => ({
    products, productsLoading: loading, productsError: error, reloadProducts: loadProducts,
    syncCatalog,
    mergeProducts,
    forgetProducts,
    movements, addProduct, updateProduct, deleteProduct, recordMovement,
    categories, categoriesLoading, categoriesError, reloadCategories: loadCategories,
    addCategory, updateCategory, deleteCategory, applyProductCategoryMoves,
  }), [
    products, loading, error, loadProducts, syncCatalog, mergeProducts, forgetProducts,
    movements, addProduct, updateProduct, deleteProduct, recordMovement,
    categories, categoriesLoading, categoriesError, loadCategories,
    addCategory, updateCategory, deleteCategory, applyProductCategoryMoves,
  ]);

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error('useProducts must be used within ProductsProvider');
  return ctx;
}
